import type {
  ChangeLogEntry,
  HistoryEntityType,
  MaintenanceDashboardMetrics,
  MonthlyCloseExecutionResult,
  MonthlyClosePreview,
  PropertiesSummary,
} from '../types';
import { getViteApiUrl } from '../config/env';
import { setSuppressSessionRestore } from '../config/sessionRestore';

/** Base del API. Las peticiones usan `credentials: 'include'` (equivalente a Axios `withCredentials: true`) para enviar cookies HttpOnly. */
const API_BASE_URL = getViteApiUrl();

/** Respuesta de error con status HTTP (p. ej. 409). Evita tratarlo como fallo inesperado en consola. */
export class HttpResponseError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = 'HttpResponseError';
  }
}

/**
 * Convierte coverImage (ruta o URL) en URL absoluta para mostrar imágenes de portada.
 *
 * Las imágenes de portada ahora se almacenan como archivos físicos bajo
 * /uploads/properties/ y se sirven vía el middleware static de Express:
 *   express.static('/api/uploads') → /api/uploads/properties/{file}
 *
 * Casos soportados:
 *   data:image/...              → se devuelve tal cual (legacy, será eliminado)
 *   https://...                 → se devuelve tal cual (URL externa)
 *   /uploads/properties/x.jpg   → /api/uploads/properties/x.jpg
 *   /api/uploads/properties/x.jpg → URL absoluta completa
 *   null / undefined / ''        → ''
 */
export function getPropertyCoverImageUrl(coverImage: string | undefined): string {
  if (!coverImage || typeof coverImage !== 'string') return '';
  const s = coverImage.trim();
  if (!s) return '';

  if (s.startsWith('data:')) return s;
  if (s.startsWith('https://') || s.startsWith('http://')) return s;

  const apiRoot = API_BASE_URL.replace(/\/$/, '');
  const baseWithApi = apiRoot.endsWith('/api') ? apiRoot : `${apiRoot}/api`;

  if (s.startsWith('/api/uploads/')) {
    return `${apiRoot}${s}`;
  }

  const normalizedPath = s.startsWith('/') ? s : `/${s}`;
  if (normalizedPath.startsWith('/uploads/')) {
    return `${baseWithApi}${normalizedPath}`;
  }

  return apiRoot + (s.startsWith('/') ? s : `/${s}`);
}

/**
 * URL absoluta para descargar archivos bajo `/uploads/...` (estados de cuenta, documentos, etc.).
 * En producción el backend sirve estos archivos vía `express.static` en `/api/uploads` (ver backend main.ts).
 * Si solo se concatena el origen del SPA (`in.*`), la petición va a Nginx del frontend y devuelve 404.
 */
export function getPropertyDocumentDownloadUrl(fileUrl: string | undefined): string {
  if (!fileUrl || typeof fileUrl !== 'string') return '';
  const s = fileUrl.trim();
  if (!s) return '';

  const apiRoot = API_BASE_URL.replace(/\/$/, '');
  /** Backend sirve estáticos en `/api/uploads` (express.static en main.ts). Usar siempre esa base. */
  const baseWithApi = apiRoot.endsWith('/api') ? apiRoot : `${apiRoot}/api`;
  const absoluteUploadsUrl = (pathname: string, search: string): string =>
    `${baseWithApi}${pathname}${search}`;

  if (s.startsWith('http://') || s.startsWith('https://')) {
    try {
      const u = new URL(s);
      if (u.pathname.startsWith('/uploads/')) {
        return absoluteUploadsUrl(u.pathname, u.search);
      }
    } catch {
      return s;
    }
    return s;
  }

  const path = s.startsWith('/') ? s : `/${s}`;
  if (path.startsWith('/uploads/')) {
    return absoluteUploadsUrl(path, '');
  }
  return `${apiRoot}${path}`;
}

/**
 * URL absoluta para mostrar archivos bajo `/uploads/...` (reportes de mantenimiento, etc.).
 * Usa `VITE_API_URL` + ruta → p. ej. `https://admin.jnugroup.com.mx/api/uploads/maintenance-reports/x.png`.
 * (No usar solo `https://admin.../uploads/...` sin `/api` salvo que Nginx tenga `location ^~ /uploads/` al backend.)
 */
export function getUploadsPublicUrl(fileUrl: string | undefined): string {
  if (!fileUrl || typeof fileUrl !== 'string') return '';
  const s = fileUrl.trim();
  if (!s) return '';
  if (s.startsWith('blob:') || s.startsWith('data:')) return s;
  return getPropertyDocumentDownloadUrl(s);
}

/** Placeholder SVG en base64 para cuando la imagen no carga (evita icono roto) */
export const PROPERTY_IMAGE_PLACEHOLDER =
  "data:image/svg+xml," + encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" stroke-width="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>'
  );

class ApiService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    const url = `${this.baseUrl}${path}`;

    // Si el body es FormData, no establecer Content-Type (el navegador lo hace automáticamente con el boundary)
    const isFormData = options.body instanceof FormData;

    const headers: Record<string, string> = {};

    // Anti-caché: evita que el navegador/proxy sirva respuestas viejas
    headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
    headers['Pragma'] = 'no-cache';
    headers['Expires'] = '0';

    if (!isFormData) {
      headers['Content-Type'] = 'application/json';
    }

    if (!isFormData && options.headers) {
      Object.assign(headers, options.headers as Record<string, string>);
    }
    if (isFormData && options.headers) {
      Object.assign(headers, options.headers as Record<string, string>);
      delete headers['Content-Type'];
    }

    const config: RequestInit = {
      ...options,
      cache: 'no-store',
      credentials: 'include',
      headers,
    };

    try {
      const response = await fetch(url, config);
      
      // Verificar si la respuesta es HTML (502/503 de Nginx o backend caído)
      const contentType = response.headers.get('content-type');
      if (contentType && !contentType.includes('application/json')) {
        if (contentType.includes('text/html')) {
          const status = response.status;
          if (status === 502 || status === 503 || status === 504) {
            throw new Error(
              'El servidor no está disponible en este momento. Intenta de nuevo en unos minutos.'
            );
          }
          throw new Error(
            `El servidor devolvió una respuesta inesperada. Verifica que el backend esté activo en ${this.baseUrl}.`
          );
        }
      }
      
      if (!response.ok) {
        if (response.status === 401) {
          const isAuthRefresh = endpoint.replace(/^\//, '') === 'auth/refresh';
          if (!isAuthRefresh) {
            try {
              const refreshResponse = await fetch(`${this.baseUrl}/auth/refresh`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({}),
              });

              if (refreshResponse.ok) {
                await refreshResponse.json().catch(() => ({}));
                const retryResponse = await fetch(url, {
                  ...options,
                  cache: 'no-store',
                  credentials: 'include',
                  headers,
                });

                if (!retryResponse.ok) {
                  throw new Error(`HTTP error! status: ${retryResponse.status}`);
                }

                if (retryResponse.status === 204) {
                  return null as T;
                }

                const text = await retryResponse.text();
                if (!text) {
                  return null as T;
                }

                try {
                  return JSON.parse(text);
                } catch {
                  throw new Error('La respuesta del servidor no es JSON válido');
                }
              }
            } catch {
              /* cae al logout abajo */
            }
          }
          try {
            await fetch(`${this.baseUrl}/auth/logout`, {
              method: 'POST',
              credentials: 'include',
            });
          } catch {
            /* ignore */
          }
          setSuppressSessionRestore();
          window.location.href = '/login';
          throw new Error('Sesión expirada. Por favor inicia sesión nuevamente.');
        }

        // Para otros errores, procesar normalmente
        let error;
        try {
          error = await response.json();
        } catch {
          const text = await response.text();
          throw new Error(text || response.statusText || `HTTP error! status: ${response.status}`);
        }
        const msg = error.message || response.statusText || `HTTP error! status: ${response.status}`;
        if (response.status >= 400 && response.status < 500) {
          throw new HttpResponseError(msg, response.status);
        }
        throw new Error(msg);
      }

      // Si la respuesta está vacía (204 No Content), retornar null
      if (response.status === 204) {
        return null as T;
      }

      // Verificar que la respuesta sea JSON antes de parsear
      const text = await response.text();
      if (!text) {
        return null as T;
      }
      
      try {
        return JSON.parse(text);
      } catch (parseError) {
        console.error(`Error parsing JSON from ${url}:`, text.substring(0, 200));
        throw new Error(
          `La respuesta del servidor no es JSON válido. Verifica que el backend esté corriendo correctamente en ${this.baseUrl}`
        );
      }
    } catch (error) {
      if (!(error instanceof HttpResponseError)) {
        console.error(`API Error [${endpoint}]:`, error);
      }
      throw error;
    }
  }

  // Investors
  async getInvestors(params?: {
    search?: string;
    status?: 'active' | 'inactive';
    propertyId?: string;
    page?: number;
    limit?: number;
  }) {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append('search', params.search);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.propertyId) queryParams.append('propertyId', params.propertyId);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    
    const queryString = queryParams.toString();
    const url = queryString ? `/investors?${queryString}` : '/investors';
    return this.request<any>(url);
  }

  async getInvestor(id: string) {
    return this.request<any>(`/investors/${id}`);
  }

  async getInvestorTimeline(investorId: string) {
    return this.request<
      Array<{
        id: string;
        date: string;
        type: string;
        title: string;
        description: string;
        icon?: string;
        status?: string;
      }>
    >(`/investors/${investorId}/timeline`);
  }

  async uploadAccountStatementFile(file: File): Promise<{ fileUrls: string[] }> {
    const formData = new FormData();
    formData.append('files', file);
    return this.request<{ fileUrls: string[] }>('/account-statements/upload', {
      method: 'POST',
      body: formData,
    });
  }

  async createAccountStatement(data: Record<string, unknown>) {
    return this.request<unknown>('/account-statements', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async createPropertyAccountStatement(data: Record<string, unknown>) {
    return this.request<unknown>('/account-statements/property', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getInvestorStatements(investorId: string) {
    return this.request<unknown[]>(`/account-statements/investor/${investorId}`);
  }

  async getMyAccountStatements() {
    return this.request<unknown[]>(`/account-statements/me`);
  }

  async getPropertyAccountStatements(propertyId: string) {
    return this.request<unknown[]>(`/account-statements/property/${propertyId}`);
  }

  async getAnnualAccountStatement(investorId: string, propertyId: string, year: number) {
    const q = new URLSearchParams({
      investorId,
      propertyId,
      year: String(year),
    });
    return this.request<{
      year: number;
      totalIncome: number;
      totalExpenses: number;
      totalNetIncome: number;
      totalProfit: number;
      months: unknown[];
    }>(`/account-statements/annual?${q.toString()}`);
  }

  async getInvestorROI(investorId: string) {
    return this.request<unknown>(`/account-statements/investor/${investorId}/roi`);
  }

  async getMyInvestorROI() {
    return this.request<unknown>(`/account-statements/me/roi`);
  }

  async deleteAccountStatement(id: string) {
    return this.request<void>(`/account-statements/${id}`, {
      method: 'DELETE',
    });
  }

  /** Elimina el estado mensual de la propiedad (property_account_statements) y filas por inversionista. */
  async deletePropertyAccountStatementPeriod(id: string) {
    return this.request<{ deleted: boolean }>(`/account-statements/property-period/${id}`, {
      method: 'DELETE',
    });
  }

  async previewMonthlyClose(data: { propertyId: string; year: number; month: number }) {
    return this.request<MonthlyClosePreview>('/monthly-close/preview', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async executeMonthlyClose(data: { propertyId: string; year: number; month: number }) {
    return this.request<MonthlyCloseExecutionResult>('/monthly-close/execute', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getChangeLogs(params: { entityId: string; entityType: HistoryEntityType; limit?: number }) {
    const q = new URLSearchParams();
    q.set('entityId', params.entityId);
    q.set('entityType', params.entityType);
    if (params.limit != null) q.set('limit', String(params.limit));
    return this.request<ChangeLogEntry[]>(`/change-logs?${q.toString()}`);
  }

  /** Perfil del inversionista logueado (solo para rol Inversor). */
  async getMyInvestorProfile() {
    return this.request<any>('/investors/me');
  }

  /** Propiedad + documentos si el inversionista tiene inversión activa o completada en ella. */
  async getMyInvestorProperty(propertyId: string) {
    return this.request<any>(`/investors/me/properties/${propertyId}`);
  }

  /** Perfil general del usuario logueado (auth/me). */
  async getMyUserProfile() {
    return this.request<any>('/auth/me');
  }

  /** Perfil del inquilino logueado (portal de inquilino). */
  async getMyTenantProfile() {
    return this.request<any>('/tenants/me');
  }

  async getMyTenantPayments() {
    return this.request<any>('/tenants/me/payments');
  }

  async getMyTenantMaintenanceReports() {
    return this.request<any>('/tenants/me/maintenance-reports');
  }

  async getMyTenantRenewalRequests() {
    return this.request<any>('/tenants/me/renewal-requests');
  }

  async addMyInvestorEmail(email: string) {
    return this.request<any>('/investors/me/emails', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async removeMyInvestorEmail(email: string) {
    return this.request<any>(`/investors/me/emails/${encodeURIComponent(email)}`, {
      method: 'DELETE',
    });
  }

  /** Completar/crear perfil de inversionista cuando no existe (POST /investors/me). */
  async completeMyInvestorProfile(data: { name?: string; phone?: string }) {
    return this.request<any>('/investors/me', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async createInvestor(data: any, files?: {
    csf?: File | null;
    idOficial?: File | null;
    kyc?: File | null;
    caratulaBancaria?: File | null;
    actaConstitutiva?: File | null;
  }) {
    const formData = new FormData();
    
    // Agregar campos del formulario
    Object.keys(data).forEach(key => {
      if (data[key] === null || data[key] === undefined || data[key] === '') {
        return;
      }
      if (key === 'additionalEmails' && Array.isArray(data[key])) {
        formData.append(key, JSON.stringify(data[key]));
        return;
      }
      formData.append(key, data[key]);
    });

    // Agregar archivos si existen
    if (files) {
      if (files.csf) formData.append('csf', files.csf);
      if (files.idOficial) formData.append('idOficial', files.idOficial);
      if (files.kyc) formData.append('kyc', files.kyc);
      if (files.caratulaBancaria) formData.append('caratulaBancaria', files.caratulaBancaria);
      if (files.actaConstitutiva) formData.append('actaConstitutiva', files.actaConstitutiva);
    }

    return this.request<any>('/investors', {
      method: 'POST',
      body: formData,
    });
  }

  async updateInvestor(id: string, data: any, files?: {
    csf?: File | null;
    idOficial?: File | null;
    kyc?: File | null;
    caratulaBancaria?: File | null;
    actaConstitutiva?: File | null;
  }) {
    const formData = new FormData();
    
    // Agregar campos del formulario
    Object.keys(data).forEach(key => {
      if (data[key] === null || data[key] === undefined || data[key] === '') {
        return;
      }
      if (key === 'additionalEmails' && Array.isArray(data[key])) {
        formData.append(key, JSON.stringify(data[key]));
        return;
      }
      formData.append(key, data[key]);
    });

    // Agregar archivos si existen
    if (files) {
      if (files.csf) formData.append('csf', files.csf);
      if (files.idOficial) formData.append('idOficial', files.idOficial);
      if (files.kyc) formData.append('kyc', files.kyc);
      if (files.caratulaBancaria) formData.append('caratulaBancaria', files.caratulaBancaria);
      if (files.actaConstitutiva) formData.append('actaConstitutiva', files.actaConstitutiva);
    }

    return this.request<any>(`/investors/${id}`, {
      method: 'PATCH',
      body: formData,
    });
  }

  async deleteInvestor(id: string) {
    return this.request<void>(`/investors/${id}`, {
      method: 'DELETE',
    });
  }

  async addInvestorDocument(investorId: string, documentData: any) {
    return this.request<any>(`/investors/${investorId}/documents`, {
      method: 'POST',
      body: JSON.stringify(documentData),
    });
  }

  async removeInvestorDocument(documentId: string) {
    return this.request<void>(`/investors/documents/${documentId}`, {
      method: 'DELETE',
    });
  }

  async removeMyInvestorDocument(documentId: string) {
    return this.request<void>(`/investors/me/documents/${documentId}`, {
      method: 'DELETE',
    });
  }

  async addMyInvestorDocument(documentData: any) {
    return this.request<any>('/investors/me/documents', {
      method: 'POST',
      body: JSON.stringify(documentData),
    });
  }

  // Properties
  /** Verifica si el API responde con JSON (útil para mostrar mensaje antes de cargar datos) */
  async checkHealth(): Promise<boolean> {
    try {
      const res = await fetch(`${this.baseUrl}/health`);
      const ct = res.headers.get('content-type') || '';
      return res.ok && ct.includes('application/json');
    } catch {
      return false;
    }
  }

  /** Propiedades paginadas. Por defecto page=1, limit=20. Para listas completas usa limit=500. */
  async getProperties(params?: { page?: number; limit?: number }) {
    const q = new URLSearchParams();
    if (params?.page != null) q.set('page', String(params.page));
    if (params?.limit != null) q.set('limit', String(params.limit));
    const query = q.toString();
    return this.request<{ data: any[]; total: number; page: number; limit: number; totalPages: number }>(
      query ? `/properties?${query}` : '/properties',
    );
  }

  async getPropertiesSummary(): Promise<PropertiesSummary> {
    return this.request<PropertiesSummary>('/properties/summary');
  }

  async getProperty(id: string) {
    return this.request<any>(`/properties/${id}`);
  }

  async getPropertyTimeline(propertyId: string) {
    return this.request<
      Array<{
        id: string;
        date: string;
        type: string;
        title: string;
        description: string;
        icon?: string;
        status?: string;
      }>
    >(`/properties/${propertyId}/timeline`);
  }

  async createProperty(data: any) {
    return this.request<any>('/properties', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateProperty(id: string, data: any) {
    return this.request<any>(`/properties/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteProperty(id: string) {
    return this.request<void>(`/properties/${id}`, {
      method: 'DELETE',
    });
  }

  async addPropertyDocument(propertyId: string, documentData: any) {
    return this.request<any>(`/properties/${propertyId}/documents`, {
      method: 'POST',
      body: JSON.stringify(documentData),
    });
  }

  async deletePropertyDocument(documentId: string) {
    return this.request<void>(`/properties/documents/${documentId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Sube la imagen de portada por multipart/form-data y devuelve la ruta para guardar en coverImage.
   */
  async uploadPropertyCoverImage(file: File): Promise<{ coverImage: string }> {
    const formData = new FormData();
    formData.append('coverImage', file);
    return this.request<{ coverImage: string }>('/properties/upload-cover-image', {
      method: 'POST',
      body: formData,
    });
  }

  /** Sube un documento (PDF/imagen) de propiedad y devuelve la URL para guardar en el documento. */
  async uploadPropertyDocument(file: File): Promise<{ fileUrl: string }> {
    const formData = new FormData();
    formData.append('file', file);
    return this.request<{ fileUrl: string }>('/properties/upload-document', {
      method: 'POST',
      body: formData,
    });
  }

  // Property maintenances (Administración de inmuebles)
  /** Mantenimientos paginados. Filtro opcional por propertyId. */
  async getPropertyMaintenances(params?: { propertyId?: string; page?: number; limit?: number }) {
    const q = new URLSearchParams();
    if (params?.propertyId != null) q.set('propertyId', params.propertyId);
    if (params?.page != null) q.set('page', String(params.page));
    if (params?.limit != null) q.set('limit', String(params.limit));
    const query = q.toString();
    return this.request<{ data: any[]; total: number; page: number; limit: number; totalPages: number }>(
      query ? `/property-maintenances?${query}` : '/property-maintenances',
    );
  }

  async getPropertyMaintenance(id: string) {
    return this.request<any>(`/property-maintenances/${id}`);
  }

  async createPropertyMaintenance(data: any) {
    return this.request<any>('/property-maintenances', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updatePropertyMaintenance(id: string, data: any) {
    return this.request<any>(`/property-maintenances/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deletePropertyMaintenance(id: string) {
    return this.request<void>(`/property-maintenances/${id}`, {
      method: 'DELETE',
    });
  }

  /** Historial de mantenimientos de una propiedad. */
  async getPropertyMaintenancesByProperty(propertyId: string) {
    return this.request<any[]>(`/properties/${propertyId}/maintenances`);
  }

  // Maintenance reports (Mantenimiento de inmuebles)
  async getMaintenanceReports(params?: { page?: number; limit?: number }) {
    const q = new URLSearchParams();
    if (params?.page != null) q.set('page', String(params.page));
    if (params?.limit != null) q.set('limit', String(params.limit));
    const query = q.toString();
    return this.request<{ data: any[]; total: number; page: number; limit: number; totalPages: number }>(
      query ? `/maintenance-reports?${query}` : '/maintenance-reports',
    );
  }

  async getAllMaintenanceReports(params?: { page?: number; limit?: number; propertyId?: string; status?: string }) {
    const q = new URLSearchParams();
    if (params?.page != null) q.set('page', String(params.page));
    if (params?.limit != null) q.set('limit', String(params.limit));
    if (params?.propertyId) q.set('propertyId', params.propertyId);
    if (params?.status) q.set('status', params.status);
    const query = q.toString();
    return this.request<{ data: any[]; total: number; page: number; limit: number; totalPages: number }>(
      query ? `/maintenance-reports/all?${query}` : '/maintenance-reports/all',
    );
  }

  async getMaintenanceReport(id: string) {
    return this.request<any>(`/maintenance-reports/${id}`);
  }

  async getMaintenanceReportTimeline(reportId: string) {
    return this.request<
      Array<{
        id: string;
        date: string;
        type: string;
        title: string;
        description: string;
        icon?: string;
        status?: string;
      }>
    >(`/maintenance-reports/${reportId}/timeline`);
  }

  async getMaintenanceReportUpdates(id: string) {
    return this.request<any[]>(`/maintenance-reports/${id}/updates`);
  }

  async getMaintenanceReportComments(id: string) {
    return this.request<any[]>(`/maintenance-reports/${id}/comments`);
  }

  async postMaintenanceReportComment(id: string, data: { content: string; photos?: string[] }) {
    return this.request<any>(`/maintenance-reports/${id}/comments`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async patchMaintenanceReportStatus(id: string, status: string) {
    return this.request<any>(`/maintenance-reports/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  }

  async patchMaintenanceReportQuotation(
    id: string,
    data: { isFree: boolean; amount?: number; notes?: string },
  ) {
    return this.request<any>(`/maintenance-reports/${id}/quotation`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async approveMaintenanceReportQuotation(id: string) {
    return this.request<any>(`/maintenance-reports/${id}/quotation/approve`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  /** Bitácora técnico (asignado o administración): lista de avances. */
  async getMaintenanceTechnicianLogs(reportId: string) {
    return this.request<
      Array<{
        id: string;
        reportId: string;
        userId: string;
        content: string;
        images: string[];
        createdAt: string;
        user?: { id: string; name: string; email: string };
      }>
    >(`/maintenance/${reportId}/logs`);
  }

  /** Registrar avance con fotos (multipart). */
  async postMaintenanceTechnicianLog(reportId: string, content: string, files: File[]) {
    const formData = new FormData();
    formData.append('content', content);
    for (const f of files) formData.append('files', f);
    return this.request<{ log: unknown; reportStatus: string }>(`/maintenance/${reportId}/logs`, {
      method: 'POST',
      body: formData,
    });
  }

  /** Cerrar trabajo (en curso → resuelto). */
  async patchMaintenanceComplete(reportId: string, finalComment: string) {
    return this.request<{ log: unknown; status: string }>(`/maintenance/${reportId}/complete`, {
      method: 'PATCH',
      body: JSON.stringify({ finalComment }),
    });
  }

  /** Comentarios de trabajo (portal inquilino, solo lectura). */
  async getTenantMaintenanceReportComments(reportId: string) {
    return this.request<any[]>(`/tenants/me/maintenance-reports/${reportId}/comments`);
  }

  async createMaintenanceReport(data: any) {
    return this.request<any>('/maintenance-reports', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateMaintenanceReport(id: string, data: any) {
    return this.request<any>(`/maintenance-reports/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteMaintenanceReport(id: string) {
    return this.request<void>(`/maintenance-reports/${id}`, {
      method: 'DELETE',
    });
  }

  async uploadMaintenanceReportImages(files: File[]): Promise<{ fileUrls: string[] }> {
    const formData = new FormData();
    for (const f of files) formData.append('files', f);
    return this.request<{ fileUrls: string[] }>('/maintenance-reports/upload', {
      method: 'POST',
      body: formData,
    });
  }

  async uploadPropertyManagementImages(files: File[]): Promise<{ fileUrls: string[] }> {
    const formData = new FormData();
    for (const f of files) formData.append('files', f);
    return this.request<{ fileUrls: string[] }>('/property-maintenances/upload', {
      method: 'POST',
      body: formData,
    });
  }

  async createMaintenanceReportUpdate(
    id: string,
    data: { action: string; description: string; images?: string[] },
  ) {
    return this.request<any>(`/maintenance-reports/${id}/updates`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Investments
  /** Inversiones paginadas. Por defecto page=1, limit=20. Para listas completas usa limit=500. */
  async getInvestments(params?: { page?: number; limit?: number }) {
    const q = new URLSearchParams();
    if (params?.page != null) q.set('page', String(params.page));
    if (params?.limit != null) q.set('limit', String(params.limit));
    const query = q.toString();
    return this.request<{ data: any[]; total: number; page: number; limit: number; totalPages: number }>(
      query ? `/investments?${query}` : '/investments',
    );
  }

  async getInvestment(id: string) {
    return this.request<any>(`/investments/${id}`);
  }

  async createInvestment(data: any) {
    return this.request<any>('/investments', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateInvestment(id: string, data: any) {
    return this.request<any>(`/investments/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteInvestment(id: string) {
    return this.request<void>(`/investments/${id}`, {
      method: 'DELETE',
    });
  }

  // Tenants
  /** Inquilinos paginados. Para listas completas (ej. dropdown) usa limit: 500. */
  async getTenants(params?: { page?: number; limit?: number; search?: string; status?: string; propertyId?: string }) {
    const q = new URLSearchParams();
    if (params?.page != null) q.set('page', String(params.page));
    if (params?.limit != null) q.set('limit', String(params.limit));
    if (params?.search) q.set('search', params.search);
    if (params?.status) q.set('status', params.status);
    if (params?.propertyId) q.set('propertyId', params.propertyId);
    const query = q.toString();
    return this.request<{ data: any[]; total: number; page: number; limit: number; totalPages: number }>(
      query ? `/tenants?${query}` : '/tenants',
    );
  }

  async createTenantAccount(tenantId: string, data: { email?: string }) {
    return this.request<any>(`/tenants/${tenantId}/create-account`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async assignTenantManager(tenantId: string, managerUserId: string | null) {
    return this.request<any>(`/tenants/${tenantId}/assign-manager`, {
      method: 'PATCH',
      body: JSON.stringify({ managerUserId }),
    });
  }

  async getTenant(id: string) {
    return this.request<any>(`/tenants/${id}`);
  }

  async getTenantTimeline(tenantId: string) {
    return this.request<
      Array<{
        id: string;
        date: string;
        type: string;
        title: string;
        description: string;
        icon?: string;
        status?: string;
      }>
    >(`/tenants/${tenantId}/timeline`);
  }

  async createTenant(data: any) {
    return this.request<any>('/tenants', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async revertTenantToProspect(tenantId: string) {
    return this.request<{ ok: boolean; prospectId: string | null }>(`/tenants/${tenantId}/revert-to-prospect`, {
      method: 'POST',
    });
  }

  async releaseStuckUnit(tenantId: string) {
    return this.request<{ released: number }>(`/tenants/${tenantId}/release-unit`, {
      method: 'POST',
    });
  }

  async updateTenant(id: string, data: any) {
    return this.request<any>(`/tenants/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteTenant(id: string) {
    return this.request<void>(`/tenants/${id}`, {
      method: 'DELETE',
    });
  }

  async cancelTenantContract(
    id: string,
    body: {
      cancellationDate: string;
      reason: string;
      penaltyWaivedInternal: boolean;
      penaltyAmount?: number;
      assignedUserId?: string;
    },
  ) {
    return this.request<any>(`/tenants/${id}/cancel-contract`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async expireTenantContract(id: string, body?: { expirationDate?: string }) {
    return this.request<any>(`/tenants/${id}/expire`, {
      method: 'PATCH',
      body: JSON.stringify(body ?? {}),
    });
  }

  async renewTenantContract(
    id: string,
    body: { newEndDate: string; newMonthlyRent?: number },
  ) {
    return this.request<any>(`/tenants/${id}/renew-contract`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  /** Solicitud de renovación desde portal de inquilino */
  async requestTenantRenewal(body: {
    message?: string;
    proposedStartDate?: string;
    proposedEndDate?: string;
  }) {
    return this.request<any>('/tenants/me/renewal-request', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  /** Crear reporte de mantenimiento desde portal de inquilino */
  async createTenantMaintenanceReport(body: {
    title: string;
    findings: string;
    images?: string[];
    priority?: 'baja' | 'media' | 'alta' | 'urgente';
  }) {
    return this.request<any>('/tenants/me/maintenance-reports', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async updateTenantDeliveryChecklist(
    id: string,
    body: {
      deliveryChecklistDone: boolean;
      deliverySigned: boolean;
      deliveryPaid: boolean;
      notes?: string;
    },
  ) {
    return this.request<any>(`/tenants/${id}/delivery-checklist`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  async completeTenantDeliveryChecklistManually(id: string, notes?: string) {
    return this.request<any>(`/tenants/${id}/delivery-checklist/manual-complete`, {
      method: 'PATCH',
      body: JSON.stringify({ notes }),
    });
  }

  // Delivery Inspection
  async createDeliveryInspection(
    tenantId: string,
    assignedUserId: string,
    options?: { type?: 'entry' | 'exit' },
  ) {
    return this.request<any>(`/tenants/${tenantId}/delivery-inspection`, {
      method: 'POST',
      body: JSON.stringify({
        assignedUserId,
        ...(options?.type ? { type: options.type } : {}),
      }),
    });
  }

  async getDeliveryInspection(tenantId: string, type: 'entry' | 'exit' = 'entry') {
    const q = type === 'exit' ? '?type=exit' : '';
    return this.request<any>(`/tenants/${tenantId}/delivery-inspection${q}`);
  }

  async getDeliveryInspectionById(inspectionId: string) {
    return this.request<any>(`/tenants/delivery-inspections/${inspectionId}`);
  }

  async getMyDeliveryInspections(params?: { page?: number; limit?: number }) {
    const q = new URLSearchParams();
    if (params?.page != null) q.set('page', String(params.page));
    if (params?.limit != null) q.set('limit', String(params.limit));
    const query = q.toString();
    return this.request<{ data: any[]; total: number; page: number; limit: number; totalPages: number }>(
      query ? `/tenants/delivery-inspections/my?${query}` : '/tenants/delivery-inspections/my',
    );
  }

  async completeDeliveryInspection(
    inspectionId: string,
    data: { checklistData: any; notes?: string; photos?: string[] },
  ) {
    return this.request<any>(`/tenants/delivery-inspections/${inspectionId}/complete`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  /** Anexa fotos a la inspección de entrega/salida de un inquilino; si nunca existió una, se crea ya completada. */
  async addDeliveryInspectionPhotos(tenantId: string, type: 'entry' | 'exit', photos: string[]) {
    const q = type === 'exit' ? '?type=exit' : '';
    return this.request<any>(`/tenants/${tenantId}/delivery-inspection/photos${q}`, {
      method: 'POST',
      body: JSON.stringify({ photos }),
    });
  }

  async getInspectionCharges(inspectionId: string) {
    return this.request<any[]>(`/tenants/delivery-inspections/${inspectionId}/charges`);
  }

  async addInspectionCharge(inspectionId: string, data: { description: string; amount: number }) {
    return this.request<any>(`/tenants/delivery-inspections/${inspectionId}/charges`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async markInspectionChargePaid(chargeId: string, isPaid: boolean) {
    return this.request<any>(`/tenants/delivery-inspections/charges/${chargeId}/paid`, {
      method: 'PATCH',
      body: JSON.stringify({ isPaid }),
    });
  }

  async deleteInspectionCharge(chargeId: string) {
    return this.request<void>(`/tenants/delivery-inspections/charges/${chargeId}`, {
      method: 'DELETE',
    });
  }

  async addTenantDocument(
    tenantId: string,
    documentData: { title: string; fileUrl?: string; fileType?: string; documentType?: 'contract' | 'deposit_receipt' | 'id' | 'other' },
  ) {
    return this.request<any>(`/tenants/${tenantId}/documents`, {
      method: 'POST',
      body: JSON.stringify(documentData),
    });
  }

  async deleteTenantDocument(documentId: string) {
    return this.request<void>(`/tenants/documents/${documentId}`, {
      method: 'DELETE',
    });
  }

  // Pagos de renta (por inquilino / mes)
  async getTenantPaymentsSummary(params?: { page?: number; limit?: number; arrearsOnly?: boolean }) {
    const q = new URLSearchParams();
    if (params?.page != null) q.set('page', String(params.page));
    if (params?.limit != null) q.set('limit', String(params.limit));
    if (params?.arrearsOnly === true) q.set('arrearsOnly', 'true');
    const query = q.toString();
    return this.request<{
      data: Array<{
        tenantId: string;
        name: string;
        propertyName: string;
        unitNumber: string;
        monthlyRent: number;
        tenantStatus: string;
        hasArrears: boolean;
        unpaidMonthLabels: string[];
      }>;
      total: number;
      page: number;
      limit: number;
      totalPages: number;
      arrearsOnly: boolean;
    }>(query ? `/tenant-payments/summary?${query}` : '/tenant-payments/summary');
  }

  async getTenantPaymentState(tenantId: string) {
    return this.request<{
      tenantId: string;
      monthlyRent: number;
      hasArrears: boolean;
      unpaidMonthLabels: string[];
      latestByPeriod: Array<{
        periodYear: number;
        periodMonth: number;
        status: string;
        notes: string | null;
        lastRecordedAt: string | null;
        paymentDate?: string;
        transactionKind?: string;
        paidAmount?: number | null;
      }>;
      history: Array<{
        id: string;
        tenantId: string;
        periodYear: number;
        periodMonth: number;
        status: string;
        notes: string | null;
        createdAt: string | null;
        paymentDate?: string;
        transactionKind?: string;
        receiptUrl?: string | null;
        paidAmount?: number | null;
      }>;
    }>(`/tenant-payments/tenant/${tenantId}`);
  }

  async createTenantMonthlyPayment(
    tenantId: string,
    body: {
      paymentDate: string;
      transactionKind?: 'rent' | 'deposit';
      status: 'paid' | 'unpaid' | 'partial';
      notes?: string;
      receiptUrl?: string;
      paidAmount?: number;
    },
  ) {
    return this.request<any>(`/tenant-payments/tenant/${tenantId}`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  // Prospects
  /** Prospectos paginados. Para listas completas (ej. dropdown) usa limit: 500. */
  async getProspects(params?: { page?: number; limit?: number; solicitudContestada?: boolean }) {
    const q = new URLSearchParams();
    if (params?.page != null) q.set('page', String(params.page));
    if (params?.limit != null) q.set('limit', String(params.limit));
    if (params?.solicitudContestada === true) q.set('solicitudContestada', 'true');
    const query = q.toString();
    return this.request<{ data: any[]; total: number; page: number; limit: number; totalPages: number }>(
      query ? `/prospects?${query}` : '/prospects',
    );
  }

  async getProspect(id: string) {
    return this.request<any>(`/prospects/${id}`);
  }

  async createProspect(data: any) {
    return this.request<any>('/prospects', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateProspect(id: string, data: any) {
    return this.request<any>(`/prospects/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteProspect(id: string) {
    return this.request<void>(`/prospects/${id}`, {
      method: 'DELETE',
    });
  }

  // Applications
  /** Solicitudes paginadas. Para listas completas usa limit: 500. */
  async getApplications(params?: { page?: number; limit?: number }) {
    const q = new URLSearchParams();
    if (params?.page != null) q.set('page', String(params.page));
    if (params?.limit != null) q.set('limit', String(params.limit));
    const query = q.toString();
    return this.request<{ data: any[]; total: number; page: number; limit: number; totalPages: number }>(
      query ? `/applications?${query}` : '/applications',
    );
  }

  async getApplication(id: string) {
    return this.request<any>(`/applications/${id}`);
  }

  /** Link público exacto enviado por correo para llenar el formulario (/solicitud/?t=TOKEN). */
  async getSolicitudPublicLink(id: string) {
    return this.request<{ solicitudUrl: string }>(`/applications/${id}/solicitud/link`, {
      method: 'GET',
    });
  }

  async createApplication(data: any) {
    return this.request<any>('/applications', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateApplication(id: string, data: any) {
    return this.request<any>(`/applications/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteApplication(id: string) {
    return this.request<void>(`/applications/${id}`, {
      method: 'DELETE',
    });
  }

  // Units
  /** Si propertyId está definido: todas las unidades de esa propiedad (array). Si no: listado paginado. */
  async getUnits(propertyId?: string, params?: { page?: number; limit?: number }) {
    const q = new URLSearchParams();
    if (propertyId) {
      q.set('propertyId', propertyId);
      if (params?.page != null) q.set('page', String(params.page));
      // Listado por propiedad ahora es paginado; los formularios necesitan todas las unidades de la propiedad.
      q.set('limit', String(params?.limit ?? 500));
    } else {
      if (params?.page != null) q.set('page', String(params.page));
      if (params?.limit != null) q.set('limit', String(params.limit));
    }
    const query = q.toString();
    const url = query ? `/units?${query}` : '/units';
    return this.request<any[] | { data: any[]; total: number; page: number; limit: number; totalPages: number }>(url);
  }

  async getUnit(id: string) {
    return this.request<any>(`/units/${id}`);
  }

  async createUnit(data: any) {
    return this.request<any>('/units', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateUnit(id: string, data: any) {
    return this.request<any>(`/units/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteUnit(id: string) {
    return this.request<void>(`/units/${id}`, {
      method: 'DELETE',
    });
  }

  // Dashboard
  async getDashboardMetrics() {
    return this.request<any>('/dashboard/metrics');
  }

  async getRevenueData() {
    return this.request<any[]>('/dashboard/revenue');
  }

  async getOccupancyData() {
    return this.request<any[]>('/dashboard/occupancy');
  }

  async getRentPerM2Stats() {
    return this.request<{
      summary: { averageRentPerM2: number; maxRentPerM2: number; minRentPerM2: number };
      properties: Array<{ id: string; name: string; city: string; m2Total: number; totalMonthlyRent: number; rentPerM2: number }>;
    }>('/dashboard/rent-per-m2');
  }

  async getRentPerM2Units(propertyId: string) {
    const q = new URLSearchParams({ propertyId });
    return this.request<{
      property: { id: string; name: string; city: string | null };
      summary: {
        totalUnits: number;
        occupiedCount: number;
        availableCount: number;
        averageRentPerM2: number;
      };
      units: Array<{
        id: string;
        unitNumber: string;
        m2: number;
        tenantName: string | null;
        monthlyRent: number | null;
        rentPerM2: number | null;
        status: 'occupied' | 'available';
      }>;
    }>(`/dashboard/rent-per-m2/units?${q.toString()}`);
  }

  /** Todo el dashboard en una sola petición (más rápido) */
  async getDashboardAll(params?: { from?: string; to?: string }) {
    const q = new URLSearchParams();
    if (params?.from) q.set('from', params.from);
    if (params?.to) q.set('to', params.to);
    const qs = q.toString();
    return this.request<{
      metrics: any;
      revenue: any[];
      occupancy: any[];
      properties: any[];
      investments: any[];
      revenueByProperty: Record<string, number>;
      maintenance: MaintenanceDashboardMetrics;
    }>(qs ? `/dashboard/all?${qs}` : '/dashboard/all');
  }

  // Users
  /** Usuarios paginados. Para listas completas usa limit: 500. */
  async getUsers(params?: { page?: number; limit?: number; search?: string }) {
    const q = new URLSearchParams();
    if (params?.page != null) q.set('page', String(params.page));
    if (params?.limit != null) q.set('limit', String(params.limit));
    if (params?.search != null && params.search.trim() !== '') q.set('search', params.search.trim());
    const query = q.toString();
    return this.request<{ data: any[]; total: number; page: number; limit: number; totalPages: number }>(
      query ? `/users?${query}` : '/users',
    );
  }

  /** Rol en BD: `Mantenimiento` (insensible a mayúsculas en el servidor). */
  async getUsersByRole(roleName: string) {
    return this.request<Array<{ id: string; name: string; email: string; roles: { name: string } }>>(
      `/users/by-role/${encodeURIComponent(roleName)}`,
    );
  }

  getMaintenanceUsers() {
    return this.getUsersByRole('Mantenimiento');
  }

  /** Técnicos de Mantenimiento + Administración/Dirección, para asignar inspecciones de entrega/salida cuando no hay personal de mantenimiento. */
  getMaintenanceOrAdminUsers() {
    return this.request<Array<{ id: string; name: string; email: string; roles: { name: string } }>>(
      '/users/eligible-for-delivery-inspection',
    );
  }

  async getUser(id: string) {
    return this.request<any>(`/users/${id}`);
  }

  async createUser(data: any) {
    return this.request<any>('/users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateUser(id: string, data: any) {
    return this.request<any>(`/users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  /** Contraseña temporal nueva + correo (mismo flujo que alta de usuario). */
  async resendUserAccessEmail(id: string) {
    return this.request<{ message: string }>(`/users/${id}/resend-access-email`, {
      method: 'POST',
    });
  }

  async deleteUser(id: string) {
    return this.request<void>(`/users/${id}`, {
      method: 'DELETE',
    });
  }

  // Roles
  async getRoles() {
    return this.request<any[]>('/roles');
  }

  async getRole(id: string) {
    return this.request<any>(`/roles/${id}`);
  }

  async createRole(data: any) {
    return this.request<any>('/roles', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateRole(id: string, data: any) {
    return this.request<any>(`/roles/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteRole(id: string) {
    return this.request<void>(`/roles/${id}`, {
      method: 'DELETE',
    });
  }

  async getSystemNotificationEmails() {
    return this.request<{
      maintenanceNotificationEmails: string[];
      renewalNotificationEmails: string[];
      prospectNotificationEmails: string[];
      investorNotificationEmails: string[];
    }>('/system-settings/notification-emails');
  }

  async updateSystemNotificationEmails(payload: {
    maintenanceNotificationEmails: string[];
    renewalNotificationEmails: string[];
    prospectNotificationEmails: string[];
    investorNotificationEmails: string[];
  }) {
    return this.request<{
      maintenanceNotificationEmails: string[];
      renewalNotificationEmails: string[];
      prospectNotificationEmails: string[];
      investorNotificationEmails: string[];
    }>('/system-settings/notification-emails', {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  }

  async getNotificationTemplates() {
    return this.request<Record<string, { title: string; body: string }>>(
      '/system-settings/notification-templates',
    );
  }

  async updateNotificationTemplates(
    payload: Record<string, { title?: string; body?: string }>,
  ) {
    return this.request<Record<string, { title: string; body: string }>>(
      '/system-settings/notification-templates',
      { method: 'PATCH', body: JSON.stringify(payload) },
    );
  }

  async getReminderSettings() {
    return this.request<{ contractExpiryDays: number; documentExpiryDays: number; enabled: boolean }>(
      '/system-settings/reminder-settings',
    );
  }

  async updateReminderSettings(data: { contractExpiryDays?: number; documentExpiryDays?: number; enabled?: boolean }) {
    return this.request<{ contractExpiryDays: number; documentExpiryDays: number; enabled: boolean }>(
      '/system-settings/reminder-settings',
      { method: 'PATCH', body: JSON.stringify(data) },
    );
  }

  // Configuración de cuenta (contraseña y 2FA)
  async changePassword(currentPassword: string, newPassword: string) {
    return this.request<{ message: string }>('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  }

  async get2faSetup() {
    return this.request<{ qrCodeUrl: string; secret: string }>('/auth/2fa/setup');
  }

  async verify2fa(code: string) {
    return this.request<{ message: string }>('/auth/2fa/verify', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  }

  async disable2fa(password: string) {
    return this.request<{ message: string }>('/auth/2fa/disable', {
      method: 'POST',
      body: JSON.stringify({ password }),
    });
  }

  async getNotifications(limit = 30) {
    return this.request<
      {
        id: string;
        type: string;
        title: string;
        body: string | null;
        maintenanceReportId: string | null;
        readAt: string | null;
        createdAt: string | null;
      }[]
    >(`/notifications?limit=${limit}`);
  }

  async getNotificationsUnreadCount() {
    return this.request<{ count: number }>('/notifications/unread-count');
  }

  async markNotificationRead(id: string) {
    return this.request<{ id: string; readAt: string | null }>(`/notifications/${id}/read`, {
      method: 'PATCH',
    });
  }

  async markAllNotificationsRead() {
    return this.request<{ ok: boolean }>('/notifications/read-all', {
      method: 'PATCH',
    });
  }

  async getPushVapidPublicKey() {
    return this.request<{ configured: boolean; publicKey: string | null }>(
      '/notifications/push/vapid-public-key',
    );
  }

  async subscribePush(body: { endpoint: string; keys: { p256dh: string; auth: string } }) {
    return this.request<{ ok: boolean }>('/notifications/push/subscribe', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async unsubscribePush(endpoint: string) {
    return this.request<{ ok: boolean }>('/notifications/push/unsubscribe', {
      method: 'POST',
      body: JSON.stringify({ endpoint }),
    });
  }
}

export const api = new ApiService();

